import type { Operator, Rule } from '@signetix/rule-engine'
import { Command as CommandPrimitive } from 'cmdk'
import { AlertCircle, ChevronsUpDown, Clock3, Database, FolderPlus, MapPin, MonitorSmartphone, Plus, SearchIcon, SlidersHorizontal, Trash2, Variable as VariableIcon, Zap, type LucideIcon } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { UNIT_SYMBOLS } from '@signetix/presentation-schema'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import type { RuleCatalogueEntry, RuleValidation, RuleValidationError } from '@/types/api'
import { useRuleCatalogue, useRuleValidation } from '../api'
import { groupCatalogue, refGroup, refLabel, refType, withEngineRefs, type RefGroup } from '../lib/catalogue'
import {
  FAMILY_LABELS,
  FAMILY_OPS,
  FRESHNESS_LABELS,
  canAddGroup,
  canNegate,
  canonicalJson,
  countLeaves,
  emptyGroup,
  emptyLeaf,
  familiesForType,
  fromRule,
  mapNode,
  opLabel,
  summarize,
  toRule,
  valueKind,
  withFamily,
  withOp,
  withRef,
  type BuilderGroup,
  type BuilderLeaf,
  type BuilderNode,
  type Family,
} from '../lib/model'
import { MAX_LEAVES } from '@signetix/rule-engine'
import { DataPathDialog } from './data-path-dialog'
import { OfflineBadge } from './offline-badge'

export interface ConditionChange {
  /** The rule when complete, else null. */
  rule: Rule | null
  complete: boolean
}

export interface ConditionBuilderProps {
  /** Committed rule (null = start empty). Changing it from outside (undo) resets the builder. */
  value: Record<string, unknown> | null
  onChange: (change: ConditionChange) => void
  disabled?: boolean
  /** Catalogue override (tests, pre-loaded); fetched otherwise. */
  catalogue?: RuleCatalogueEntry[] | null
  /** Validate complete rules with `POST /rules/validate` (default true). */
  validate?: boolean
  onValidation?: (v: RuleValidation | null) => void
  /** Narrow layout (inspector). */
  compact?: boolean
  /** Hide the "In words" summary (when the caller shows it). */
  hideSummary?: boolean
  /**
   * Errors validated elsewhere (e.g. `POST /flows/validate`), with JSON pointers relative to the
   * rule (`/all/0`) — shown on the matching leaf like the builder's own validation.
   */
  externalErrors?: readonly RuleValidationError[]
  /** Offer a typed reference entry (e.g. a free `event.<path>` into a trigger payload). */
  freeRef?: FreeRefOption
  className?: string
}

export interface FreeRefOption {
  /** Heading of the entry, e.g. "Event payload path". */
  label: string
  placeholder: string
  /** Grammar the typed ref must match. */
  pattern: RegExp
  /** Prepended when the author types the path without it (e.g. `event.`). */
  prefix?: string
  hint?: string
}

/**
 * Visual condition builder (SIGNETIX_LOGIC.md §10): ALL / ANY groups (nested to the engine's
 * depth, optional NOT), typed reference picker, operators filtered by the reference type,
 * typed value inputs, a sentence summary and server validation with the offline badge.
 */
export function ConditionBuilder({ value, onChange, disabled, catalogue: given, validate = true, onValidation, compact, hideSummary, externalErrors, freeRef, className }: ConditionBuilderProps) {
  const fetched = useRuleCatalogue(given === undefined)
  const catalogue = useMemo(() => withEngineRefs(given === undefined ? fetched.data : given), [given, fetched.data])
  const typeOf = (ref: string) => refType(catalogue, ref)
  const [tree, setTree] = useState<BuilderGroup>(() => fromRule(value, typeOf))
  const lastEmitted = useRef<string>(value ? canonicalJson(value) : '')

  // Re-sync when the committed rule changes from outside (undo / another element).
  const incoming = value ? canonicalJson(value) : ''
  useEffect(() => {
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming
      setTree(fromRule(value, typeOf))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming])

  const result = useMemo(() => toRule(tree), [tree])
  const update = (next: BuilderGroup) => {
    setTree(next)
    const r = toRule(next)
    const canonical = r.rule ? canonicalJson(r.rule) : ''
    if (r.rule) lastEmitted.current = canonical
    onChange({ rule: r.rule, complete: !!r.rule })
  }

  const validation = useRuleValidation(result.rule as Record<string, unknown> | null, validate && !!result.rule)
  const v = result.rule ? (validation.data ?? null) : null
  useEffect(() => {
    onValidation?.(v)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v])

  const label = (ref: string) => refLabel(catalogue, ref)
  // Server errors are JSON pointers into the rule → the builder leaf at that pointer.
  const serverErrors = useMemo(() => {
    const byNode: Record<string, string[]> = {}
    const general: string[] = []
    for (const e of [...(v?.errors ?? []), ...(externalErrors ?? [])]) {
      const id = result.pointers[e.path] ?? Object.entries(result.pointers).find(([p]) => e.path.startsWith(`${p}/`))?.[1]
      if (id) (byNode[id] ??= []).push(e.message)
      else general.push(e.message)
    }
    return { byNode, general }
  }, [v, externalErrors, result.pointers])

  const ctx: Ctx = { root: tree, update, disabled: !!disabled, catalogue, label, typeOf, problems: result.problems, serverErrors: serverErrors.byNode, compact: !!compact, leaves: result.leaves, freeRef }

  return (
    <div className={cn('grid min-w-0 gap-2', className)} data-condition-builder="">
      {fetched.isPending && given === undefined ? <Skeleton className="h-6" /> : null}
      <GroupEditor group={tree} level={1} ctx={ctx} />
      {!hideSummary && (
        <p className="rounded-md bg-muted/50 px-2 py-1.5 text-[12px] leading-snug" aria-live="polite" data-testid="condition-summary">
          <span className="font-medium text-muted-foreground">In words: </span>
          {summarize(tree, label)}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {result.rule ? (
          v ? (
            <>
              <OfflineBadge edge={v.edge} />
              {!v.valid && <span className="text-[11.5px] font-medium text-destructive">Needs attention</span>}
            </>
          ) : validate && validation.isFetching ? (
            <span className="text-[11.5px] text-muted-foreground">Checking…</span>
          ) : null
        ) : (
          <span className="text-[11.5px] text-muted-foreground">Finish every condition to use it.</span>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground tabular">
          {result.leaves}/{MAX_LEAVES} conditions
        </span>
      </div>
      {serverErrors.general.map((m) => (
        <p key={m} role="alert" className="flex gap-1.5 text-[11.5px] text-destructive">
          <AlertCircle className="mt-px size-3.5 shrink-0" /> {m}
        </p>
      ))}
    </div>
  )
}

interface Ctx {
  root: BuilderGroup
  update: (next: BuilderGroup) => void
  disabled: boolean
  catalogue: RuleCatalogueEntry[]
  label: (ref: string) => string
  typeOf: (ref: string) => string | null
  problems: Record<string, string>
  serverErrors: Record<string, string[]>
  compact: boolean
  leaves: number
  freeRef?: FreeRefOption
}

/* ------------------------------------------------------------------ group */

function GroupEditor({ group, level, ctx, onRemove }: { group: BuilderGroup; level: number; ctx: Ctx; onRemove?: () => void }) {
  const set = (patch: Partial<BuilderGroup>) => ctx.update(mapNode(ctx.root, group.id, (n) => ({ ...(n as BuilderGroup), ...patch })))
  const replaceChild = (id: string, fn: (n: BuilderNode) => BuilderNode | null) => ctx.update(mapNode(ctx.root, id, fn))
  const full = ctx.leaves >= MAX_LEAVES
  const problem = ctx.problems[group.id]
  return (
    <div
      role="group"
      aria-label={level === 1 ? 'Conditions' : 'Condition group'}
      className={cn('grid min-w-0 gap-1.5', level > 1 && 'rounded-lg border border-dashed bg-muted/20 p-2', group.negate && 'border-solid border-warning/50')}
    >
      <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
        {group.negate && <span className="rounded bg-warning/15 px-1 text-[11px] font-semibold text-[color-mix(in_oklch,var(--warning),black_40%)]">NOT</span>}
        <span className="text-muted-foreground">Match</span>
        <select
          aria-label="Match"
          value={group.mode}
          disabled={ctx.disabled}
          onChange={(e) => set({ mode: e.target.value as 'all' | 'any' })}
          className="h-7 rounded-md border bg-card px-1.5 text-[12px] font-semibold"
        >
          <option value="all">ALL</option>
          <option value="any">ANY</option>
        </select>
        <span className="text-muted-foreground">of these</span>
        <label className="ml-auto inline-flex items-center gap-1 text-[11.5px] text-muted-foreground" title="Turn the whole group around: true when it does not match">
          <input type="checkbox" checked={group.negate} disabled={ctx.disabled || (!group.negate && !canNegate(ctx.root, group.id))} onChange={(e) => set({ negate: e.target.checked })} aria-label="Not (negate this group)" />
          NOT
        </label>
        {onRemove && (
          <Button type="button" variant="ghost" size="icon-sm" className="size-6" disabled={ctx.disabled} onClick={onRemove} aria-label="Remove group">
            <Trash2 />
          </Button>
        )}
      </div>
      {group.children.map((c, i) => (
        <div key={c.id} className="grid min-w-0 gap-1">
          {i > 0 && <span className="pl-1 text-[10.5px] font-semibold tracking-wide text-muted-foreground uppercase">{group.mode === 'all' ? 'and' : 'or'}</span>}
          {c.kind === 'group' ? (
            <GroupEditor group={c} level={level + 1} ctx={ctx} onRemove={() => replaceChild(c.id, () => null)} />
          ) : (
            <LeafEditor leaf={c} ctx={ctx} onChange={(next) => replaceChild(c.id, () => next)} onRemove={group.children.length > 1 || level > 1 ? () => replaceChild(c.id, () => null) : undefined} />
          )}
        </div>
      ))}
      {problem && <p className="text-[11.5px] text-destructive">{problem}</p>}
      <div className="flex flex-wrap gap-1">
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-[12px]" disabled={ctx.disabled || full} onClick={() => set({ children: [...group.children, emptyLeaf()] })}>
          <Plus /> Condition
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-[12px]"
          disabled={ctx.disabled || full || !canAddGroup(ctx.root, group.id)}
          title={!canAddGroup(ctx.root, group.id) ? 'Groups can be nested at most 4 levels deep' : undefined}
          onClick={() => set({ children: [...group.children, emptyGroup(group.mode === 'all' ? 'any' : 'all')] })}
        >
          <FolderPlus /> Group
        </Button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------- leaf */

const inputCls = 'h-7 min-w-0 rounded-md border bg-card px-1.5 text-[12.5px] outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50'

function LeafEditor({ leaf, ctx, onChange, onRemove }: { leaf: BuilderLeaf; ctx: Ctx; onChange: (l: BuilderLeaf) => void; onRemove?: () => void }) {
  const type = leaf.ref ? ctx.typeOf(leaf.ref) : null
  const families = leaf.ref ? familiesForType(type, leaf.ref) : []
  const kind = valueKind(leaf.family, leaf.op)
  const [options, setOptions] = useState(false)
  const errors = ctx.serverErrors[leaf.id] ?? []
  const problem = leaf.ref ? ctx.problems[leaf.id] : null
  const hasOptions = leaf.family === 'number' || leaf.family === 'text' || kind === 'needle' || (!!leaf.ref?.startsWith('data.') && leaf.family !== 'freshness')
  return (
    <div className={cn('grid min-w-0 gap-1 rounded-md border bg-card p-1.5', (errors.length > 0 || problem) && 'border-destructive/50')} data-testid="condition-leaf">
      <div className="flex min-w-0 items-center gap-1">
        <RefPicker value={leaf.ref} family={leaf.family} ctx={ctx} onPick={(ref, family) => onChange(withRef(leaf, ref, ctx.typeOf(ref), family))} />
        {onRemove && (
          <Button type="button" variant="ghost" size="icon-sm" className="size-7 shrink-0" disabled={ctx.disabled} onClick={onRemove} aria-label="Remove condition">
            <Trash2 />
          </Button>
        )}
      </div>
      {leaf.ref && leaf.family && (
        <div className={cn('flex min-w-0 flex-wrap items-center gap-1')}>
          {families.length > 1 && (
            <select aria-label="Compare as" className={cn(inputCls, 'max-w-28')} value={leaf.family} disabled={ctx.disabled} onChange={(e) => onChange(withFamily(leaf, e.target.value as Family))}>
              {families.map((f) => (
                <option key={f} value={f}>
                  {FAMILY_LABELS[f]}
                </option>
              ))}
            </select>
          )}
          <select aria-label="Comparison" className={cn(inputCls, 'max-w-40')} value={leaf.op ?? ''} disabled={ctx.disabled} onChange={(e) => onChange(withOp(leaf, e.target.value as Operator))}>
            {FAMILY_OPS[leaf.family].map((op) => (
              <option key={op} value={op}>
                {opLabel(leaf.family!, op)}
              </option>
            ))}
          </select>
          <ValueInput leaf={leaf} disabled={ctx.disabled} onChange={(value) => onChange({ ...leaf, value })} onNeedle={(needle) => onChange({ ...leaf, needle, value: '' })} />
          {hasOptions && (
            <Button type="button" variant="ghost" size="icon-sm" className={cn('size-7', options && 'bg-muted')} onClick={() => setOptions((o) => !o)} aria-label="Condition options" aria-expanded={options}>
              <SlidersHorizontal />
            </Button>
          )}
        </div>
      )}
      {options && hasOptions && (
        <div className="grid gap-1 rounded bg-muted/40 p-1.5 text-[12px]">
          {leaf.family === 'number' && (
            <label className="flex items-center gap-1.5">
              <span className="w-16 text-muted-foreground">Unit</span>
              <select aria-label="Unit" className={inputCls} value={leaf.unit ?? ''} disabled={ctx.disabled} onChange={(e) => onChange({ ...leaf, unit: e.target.value || undefined })}>
                <option value="">None</option>
                {Object.entries(UNIT_SYMBOLS).map(([k, s]) => (
                  <option key={k} value={k}>
                    {s} ({k})
                  </option>
                ))}
              </select>
            </label>
          )}
          {(leaf.family === 'text' || (kind === 'needle' && leaf.needle !== 'number')) && (
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={!!leaf.case_sensitive} disabled={ctx.disabled} onChange={(e) => onChange({ ...leaf, case_sensitive: e.target.checked || undefined })} />
              Match upper / lower case exactly
            </label>
          )}
          {leaf.ref?.startsWith('data.') && leaf.family !== 'freshness' && (
            <label className="flex items-center gap-1.5" title="By default, data older than its refresh interval makes the condition unknown.">
              <input type="checkbox" checked={leaf.freshness === 'allow_stale'} disabled={ctx.disabled} onChange={(e) => onChange({ ...leaf, freshness: e.target.checked ? 'allow_stale' : undefined })} />
              Also use stale data
            </label>
          )}
        </div>
      )}
      {problem && !errors.length && <p className="text-[11px] text-muted-foreground">{problem}</p>}
      {errors.map((m) => (
        <p key={m} role="alert" className="flex gap-1 text-[11.5px] text-destructive">
          <AlertCircle className="mt-px size-3.5 shrink-0" /> {m}
        </p>
      ))}
    </div>
  )
}

function ValueInput({ leaf, disabled, onChange, onNeedle }: { leaf: BuilderLeaf; disabled: boolean; onChange: (v: unknown) => void; onNeedle: (n: 'text' | 'number') => void }) {
  const kind = valueKind(leaf.family, leaf.op)
  const v = leaf.value
  const num = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) ? String(x) : '')
  const parseNum = (s: string): number | null => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Number(s))
  const pair = Array.isArray(v) ? v : [undefined, undefined]
  switch (kind) {
    case 'none':
      return null
    case 'number':
      return <NumberBox value={num(v)} label="Value" disabled={disabled} onChange={(s) => onChange(parseNum(s))} />
    case 'number_range':
      return (
        <span className="flex min-w-0 items-center gap-1">
          <NumberBox value={num(pair[0])} label="From" disabled={disabled} onChange={(s) => onChange([parseNum(s), pair[1]])} />
          <span className="text-[11px] text-muted-foreground">and</span>
          <NumberBox value={num(pair[1])} label="To" disabled={disabled} onChange={(s) => onChange([pair[0], parseNum(s)])} />
        </span>
      )
    case 'text':
      return <input aria-label="Value" className={cn(inputCls, 'w-32 flex-1')} value={typeof v === 'string' ? v : ''} maxLength={1024} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    case 'needle':
      return (
        <span className="flex min-w-0 flex-1 items-center gap-1">
          <select aria-label="Item type" className={cn(inputCls, 'w-20')} value={leaf.needle ?? 'text'} disabled={disabled} onChange={(e) => onNeedle(e.target.value as 'text' | 'number')}>
            <option value="text">Text</option>
            <option value="number">Number</option>
          </select>
          <input aria-label="Value" inputMode={leaf.needle === 'number' ? 'decimal' : undefined} className={cn(inputCls, 'w-24 flex-1')} value={typeof v === 'string' ? v : ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        </span>
      )
    case 'date':
      return <input type="date" aria-label="Date" className={inputCls} value={typeof v === 'string' ? v : ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    case 'date_range':
      return (
        <span className="flex min-w-0 flex-wrap items-center gap-1">
          <input type="date" aria-label="From date" className={inputCls} value={typeof pair[0] === 'string' ? pair[0] : ''} disabled={disabled} onChange={(e) => onChange([e.target.value, pair[1]])} />
          <span className="text-[11px] text-muted-foreground">and</span>
          <input type="date" aria-label="To date" className={inputCls} value={typeof pair[1] === 'string' ? pair[1] : ''} disabled={disabled} onChange={(e) => onChange([pair[0], e.target.value])} />
        </span>
      )
    case 'time':
      return <input type="time" aria-label="Time" className={inputCls} value={typeof v === 'string' ? v : ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    case 'time_range':
      return (
        <span className="flex min-w-0 items-center gap-1">
          <input type="time" aria-label="From time" className={inputCls} value={typeof pair[0] === 'string' ? pair[0] : ''} disabled={disabled} onChange={(e) => onChange([e.target.value, pair[1]])} />
          <span className="text-[11px] text-muted-foreground">and</span>
          <input type="time" aria-label="To time" className={inputCls} value={typeof pair[1] === 'string' ? pair[1] : ''} disabled={disabled} onChange={(e) => onChange([pair[0], e.target.value])} />
        </span>
      )
    case 'freshness':
      return (
        <select aria-label="Data status" className={inputCls} value={typeof v === 'string' ? v : 'expired'} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          {Object.entries(FRESHNESS_LABELS).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      )
  }
}

/** Number field that keeps partial input ("-", "1.") while typing. */
function NumberBox({ value, label, disabled, onChange }: { value: string; label: string; disabled: boolean; onChange: (s: string) => void }) {
  const [text, setText] = useState(value)
  useEffect(() => {
    if (Number(text) !== Number(value) || (value === '' && text !== '' && Number.isFinite(Number(text)))) setText(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  return (
    <input
      aria-label={label}
      inputMode="decimal"
      className={cn(inputCls, 'w-20')}
      value={text}
      disabled={disabled}
      onChange={(e) => {
        setText(e.target.value)
        onChange(e.target.value)
      }}
    />
  )
}

/* ------------------------------------------------------------- ref picker */

const GROUP_ICONS: Record<RefGroup, LucideIcon> = { variables: VariableIcon, location: MapPin, data: Database, time: Clock3, player: MonitorSmartphone, event: Zap }

function RefPicker({ value, family, ctx, onPick }: { value: string | null; family?: Family | null; ctx: Ctx; onPick: (ref: string, family: Family | null) => void }) {
  const [open, setOpen] = useState(false)
  const [source, setSource] = useState<RuleCatalogueEntry | null>(null)
  const groups = useMemo(() => groupCatalogue(ctx.catalogue), [ctx.catalogue])
  const icon = value ? GROUP_ICONS[refGroup({ ref: value, namespace: '' })] : null
  const Icon = icon
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={ctx.disabled}
            aria-label={value ? `Checks ${ctx.label(value)} — change` : 'Choose what to check'}
            className={cn('flex h-7 min-w-0 flex-1 items-center gap-1.5 rounded-md border bg-muted/30 px-1.5 text-left text-[12.5px] hover:bg-muted disabled:opacity-60', !value && 'text-muted-foreground')}
          >
            {Icon && <Icon className="size-3.5 shrink-0 text-primary" />}
            <span className="min-w-0 flex-1 truncate">{value ? ctx.label(value) : 'Choose what to check…'}</span>
            <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <CommandPrimitive className="flex flex-col">
            <div className="flex h-9 items-center gap-2 border-b px-2.5">
              <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
              <CommandPrimitive.Input placeholder="Search…" className="h-8 w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground" />
            </div>
            <CommandPrimitive.List className="max-h-72 overflow-y-auto p-1 scrollbar-thin">
              <CommandPrimitive.Empty className="py-5 text-center text-[13px] text-muted-foreground">Nothing found</CommandPrimitive.Empty>
              {groups.map((g) => {
                const GIcon = GROUP_ICONS[g.group]
                return (
                  <CommandPrimitive.Group key={g.group} heading={g.label} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-1.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10.5px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase">
                    {g.entries.map((e) => (
                      <CommandPrimitive.Item
                        key={e.ref}
                        value={e.ref}
                        keywords={[e.label, g.label]}
                        onSelect={() => {
                          setOpen(false)
                          if (e.source_id) setSource(e)
                          else onPick(e.ref, null)
                        }}
                        className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-[13px] outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                      >
                        <GIcon className="size-3.5 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate">{e.label}</span>
                        <TypeHint>{e.source_id ? 'pick a value…' : e.type}</TypeHint>
                      </CommandPrimitive.Item>
                    ))}
                  </CommandPrimitive.Group>
                )
              })}
            </CommandPrimitive.List>
          </CommandPrimitive>
          {ctx.freeRef && (
            <FreeRefEntry
              option={ctx.freeRef}
              onUse={(ref) => {
                setOpen(false)
                // A typed path keeps the current comparison when it still fits.
                onPick(ref, family ?? null)
              }}
            />
          )}
        </PopoverContent>
      </Popover>
      <DataPathDialog
        source={source}
        open={!!source}
        onOpenChange={(o) => !o && setSource(null)}
        onPick={(ref, family) => {
          setSource(null)
          onPick(ref, family)
        }}
      />
    </>
  )
}

/** Typed reference (e.g. `event.order.total`), validated against the option's grammar. */
function FreeRefEntry({ option, onUse }: { option: FreeRefOption; onUse: (ref: string) => void }) {
  const [text, setText] = useState('')
  const raw = text.trim()
  const ref = option.prefix && raw && !raw.startsWith(option.prefix) && !option.pattern.test(raw) ? `${option.prefix}${raw}` : raw
  const valid = option.pattern.test(ref)
  return (
    <form
      className="grid gap-1 border-t p-2"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (valid) onUse(ref)
      }}
    >
      <label className="text-[10.5px] font-semibold tracking-wider text-muted-foreground uppercase" htmlFor="free-ref-input">
        {option.label}
      </label>
      <div className="flex gap-1">
        <input
          id="free-ref-input"
          className={cn(inputCls, 'flex-1 font-mono')}
          value={text}
          placeholder={option.placeholder}
          aria-invalid={raw !== '' && !valid}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <Button type="submit" size="sm" variant="outline" className="h-7 px-2 text-[12px]" disabled={!valid}>
          Use
        </Button>
      </div>
      {raw !== '' && !valid ? <p className="text-[11px] text-destructive">Use letters, digits and _ separated by dots, e.g. {option.placeholder}</p> : option.hint ? <p className="text-[11px] text-muted-foreground">{option.hint}</p> : null}
    </form>
  )
}

function TypeHint({ children }: { children: ReactNode }) {
  return <span className="shrink-0 text-[10.5px] text-muted-foreground">{children}</span>
}

/** Read-only sentence for a stored rule (lists, tables). */
export function RuleSentence({ rule, catalogue, className }: { rule: unknown; catalogue?: RuleCatalogueEntry[] | null; className?: string }) {
  const fetched = useRuleCatalogue(catalogue === undefined)
  const entries = useMemo(() => withEngineRefs(catalogue === undefined ? fetched.data : catalogue), [catalogue, fetched.data])
  const text = useMemo(() => summarize(fromRule(rule, (r) => refType(entries, r)), (r) => refLabel(entries, r)), [rule, entries])
  return <span className={className}>{text}</span>
}

/** Leaf count of a stored rule. */
export function ruleLeafCount(rule: unknown): number {
  return countLeaves(fromRule(rule))
}
